import { useState, useCallback } from 'react';
import { TranslationService } from '@/api/services/translationService';
import { SupportedLanguage, TranslateParams, TranslateResult } from '@/api/types/translation';
import { logPrint } from '@/utils/my-utils';
import { toast } from 'react-hot-toast';

/**
 * 翻译配置接口
 */
export interface TranslationSettings {
  /** 是否开启自动翻译 (收到的消息) */
  autoTranslateIncoming: boolean;
  /** 是否开启自动翻译 (发送的消息) */
  autoTranslateOutgoing: boolean;
  /** 收到消息翻译的目标语言 */
  incomingTargetLanguage: string;
  /** 发送消息翻译的目标语言 */
  outgoingTargetLanguage: string;
}

/**
 * 默认翻译设置
 */
export const DEFAULT_TRANSLATION_SETTINGS: TranslationSettings = {
  autoTranslateIncoming: false,
  autoTranslateOutgoing: false,
  incomingTargetLanguage: 'zh', // 默认翻译为中文
  outgoingTargetLanguage: 'en', // 默认翻译为英文
};

/**
 * 翻译功能 Hook
 * 处理消息翻译相关的功能
 */
export function useTranslation() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [supportedLanguages, setSupportedLanguages] = useState<SupportedLanguage[]>([]);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false);

  /**
   * 获取支持的语言列表
   */
  const fetchSupportedLanguages = useCallback(async (): Promise<SupportedLanguage[]> => {
    if (supportedLanguages.length > 0) {
      return supportedLanguages;
    }

    setIsLoadingLanguages(true);
    setError(null);

    try {
      const languages = await TranslationService.getSupportedLanguages();
      setSupportedLanguages(languages);
      logPrint('获取支持的语言列表成功:', languages);
      return languages;
    } catch (err: any) {
      const errorMessage = err?.message || '获取支持的语言列表失败';
      console.error('获取支持的语言列表失败:', err);
      setError(errorMessage);
      toast.error(errorMessage);
      return [];
    } finally {
      setIsLoadingLanguages(false);
    }
  }, [supportedLanguages]);

  /**
   * 翻译文本
   * @param text 要翻译的文本
   * @param targetLanguage 目标语言代码
   * @returns 翻译结果
   */
  const translateText = useCallback(async (
    text: string,
    targetLanguage: string,
  ): Promise<TranslateResult | null> => {
    if (!text?.trim()) {
      toast.error('翻译内容不能为空');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params: TranslateParams = {
        original_content: text.trim(),
        target_language: targetLanguage,
      };

      logPrint('开始翻译:', params);
      const result = await TranslationService.translate(params);
      logPrint('翻译成功:', result);
      
      return result;
    } catch (err: any) {
      const errorMessage = err?.message || '翻译失败';
      console.error('翻译失败:', err);
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * 批量翻译文本
   * @param texts 要翻译的文本数组
   * @param targetLanguage 目标语言代码
   * @returns 翻译结果数组
   */
  const translateBatch = useCallback(async (
    texts: string[],
    targetLanguage: string,
  ): Promise<(TranslateResult | null)[]> => {
    const results: (TranslateResult | null)[] = [];
    
    for (const text of texts) {
      const result = await translateText(text, targetLanguage);
      results.push(result);
      
      // 添加短暂延迟，避免API频率限制
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    return results;
  }, [translateText]);

  /**
   * 根据翻译设置自动翻译消息
   * @param content 消息内容
   * @param settings 翻译设置
   * @param isIncoming 是否为收到的消息
   * @returns 翻译结果
   */
  const autoTranslateMessage = useCallback(async (
    content: string,
    settings: TranslationSettings,
    isIncoming: boolean
  ): Promise<TranslateResult | null> => {
    // 检查是否需要自动翻译
    const shouldTranslate = isIncoming ? settings.autoTranslateIncoming : settings.autoTranslateOutgoing;
    if (!shouldTranslate) {
      return null;
    }

    // 获取目标语言
    const targetLanguage = isIncoming ? settings.incomingTargetLanguage : settings.outgoingTargetLanguage;
    
    return translateText(content, targetLanguage);
  }, [translateText]);

  /**
   * 清除错误信息
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * 检查语言代码是否有效
   * @param languageCode 语言代码
   * @returns 是否有效
   */
  const isValidLanguageCode = useCallback((languageCode: string): boolean => {
    return supportedLanguages.some(lang => lang.code === languageCode);
  }, [supportedLanguages]);

  /**
   * 根据语言代码获取语言信息
   * @param languageCode 语言代码
   * @returns 语言信息
   */
  const getLanguageInfo = useCallback((languageCode: string): SupportedLanguage | null => {
    return supportedLanguages.find(lang => lang.code === languageCode) || null;
  }, [supportedLanguages]);


  return {
    // 状态
    isLoading,
    error,
    supportedLanguages,
    isLoadingLanguages,

    // 基础翻译方法
    translateText,
    translateBatch,
    autoTranslateMessage,

    // 语言管理
    fetchSupportedLanguages,
    isValidLanguageCode,
    getLanguageInfo,

    // 工具方法
    clearError,

    // 常量
    DEFAULT_TRANSLATION_SETTINGS
  };
}
